import { FormEvent, useEffect, useState } from "react";
import { Check, KeyRound, User, X } from "lucide-react";
import { PasswordRequirements } from "@/components/admin/PasswordRequirements";
import { PasswordInput } from "@/components/admin/PasswordInput";
import { fetchAdminProfile, updateAdminSettings } from "@/lib/api";
import { setToken } from "@/lib/auth";
import { isPasswordValid } from "@/lib/passwordValidation";

function apiErrorDetail(err: unknown, fallback: string): string {
  if (typeof err === "object" && err !== null && "response" in err) {
    const response = (err as { response?: { data?: { detail?: unknown } } }).response;
    const detail = response?.data?.detail;
    if (typeof detail === "string") return detail;
  }
  if (typeof err === "object" && err !== null && "code" in err && (err as { code?: string }).code === "ECONNABORTED") {
    return "Request timed out — check that the backend is running";
  }
  return fallback;
}

export const AdminSettingsPage = () => {
  const [currentUsername, setCurrentUsername] = useState("");
  const [username, setUsername] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const profile = await fetchAdminProfile();
        if (!cancelled) {
          setCurrentUsername(profile.username);
          setUsername(profile.username);
        }
      } catch {
        if (!cancelled) setError("Failed to load admin profile");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaved(false);

    const trimmedUsername = username.trim();
    const usernameChanged = trimmedUsername !== currentUsername;
    const passwordChanged = newPassword.length > 0;

    if (!usernameChanged && !passwordChanged) {
      setError("Change your username and/or password before saving");
      return;
    }

    if (passwordChanged && !isPasswordValid(newPassword)) {
      setError("New password does not meet all requirements");
      return;
    }

    if (passwordChanged && newPassword !== confirmPassword) {
      setError("New passwords do not match");
      return;
    }

    if (!currentPassword) {
      setError("Enter your current password to confirm changes");
      return;
    }

    setSubmitting(true);
    try {
      const { access_token } = await updateAdminSettings({
        current_password: currentPassword,
        username: usernameChanged ? trimmedUsername : undefined,
        new_password: passwordChanged ? newPassword : undefined,
      });
      setToken(access_token);
      setCurrentUsername(trimmedUsername);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setSaved(true);
    } catch (err: unknown) {
      setError(apiErrorDetail(err, "Failed to update admin settings"));
    } finally {
      setSubmitting(false);
    }
  };

  const passwordsMatch =
    newPassword.length > 0 && confirmPassword.length > 0 && newPassword === confirmPassword;

  return (
    <div data-testid="admin-settings-page">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>
        <p className="text-sm text-vg-gray mt-1">
          Update your admin username and password.
        </p>
      </div>

      <form onSubmit={onSubmit} className="max-w-xl space-y-6">
        <section className="bg-white border border-black/10 p-6 space-y-4">
          <div className="flex items-center gap-2 text-sm font-medium text-vg-ink">
            <User size={18} strokeWidth={1.75} />
            Account
          </div>

          {loading ? (
            <p className="text-sm text-vg-gray">Loading profile…</p>
          ) : (
            <>
              <div className="rounded-md border border-black/10 bg-[#f8fafc] px-4 py-3">
                <p className="text-xs font-mono-acc uppercase tracking-[0.15em] text-vg-gray">
                  Current username
                </p>
                <p
                  className="mt-1 text-lg font-semibold text-vg-ink font-mono"
                  data-testid="admin-settings-current-username"
                >
                  {currentUsername}
                </p>
              </div>

              <label className="block space-y-1.5">
                <span className="text-sm font-medium">New username</span>
                <input
                  className="w-full h-10 px-3 border border-black/15 bg-white text-sm focus:outline-none focus:ring-1 focus:ring-black"
                  value={username}
                  onChange={(e) => {
                    setUsername(e.target.value);
                    setSaved(false);
                  }}
                  placeholder={currentUsername}
                  autoComplete="username"
                  required
                  minLength={3}
                  data-testid="admin-settings-username"
                />
                <span className="text-xs text-vg-gray">
                  Leave unchanged or enter a new username (min. 3 characters).
                </span>
              </label>
            </>
          )}
        </section>

        <section className="bg-white border border-black/10 p-6 space-y-4">
          <div className="flex items-center gap-2 text-sm font-medium text-vg-ink">
            <KeyRound size={18} strokeWidth={1.75} />
            Change password
          </div>
          <p className="text-sm text-vg-gray">
            Leave new password fields blank to keep your current password.
          </p>

          <label className="block space-y-1.5">
            <span className="text-sm font-medium">Current password</span>
            <PasswordInput
              value={currentPassword}
              onChange={(value) => {
                setCurrentPassword(value);
                setSaved(false);
              }}
              autoComplete="current-password"
              required
              data-testid="admin-settings-current-password"
            />
          </label>

          <label className="block space-y-1.5">
            <span className="text-sm font-medium">New password</span>
            <PasswordInput
              value={newPassword}
              onChange={(value) => {
                setNewPassword(value);
                setSaved(false);
              }}
              autoComplete="new-password"
              minLength={8}
              data-testid="admin-settings-new-password"
            />
          </label>

          <PasswordRequirements password={newPassword} showWhenEmpty />

          <label className="block space-y-1.5">
            <span className="text-sm font-medium">Confirm new password</span>
            <PasswordInput
              value={confirmPassword}
              onChange={(value) => {
                setConfirmPassword(value);
                setSaved(false);
              }}
              autoComplete="new-password"
              minLength={8}
              data-testid="admin-settings-confirm-password"
            />
            {confirmPassword.length > 0 && (
              <p
                className={`flex items-center gap-1.5 text-xs ${
                  passwordsMatch ? "text-green-700" : "text-red-600"
                }`}
                data-testid="admin-settings-password-match"
              >
                {passwordsMatch ? <Check size={14} /> : <X size={14} />}
                {passwordsMatch ? "Passwords match" : "Passwords do not match"}
              </p>
            )}
          </label>
        </section>

        {error && (
          <p className="text-sm text-red-600" data-testid="admin-form-error">
            {error}
          </p>
        )}

        {saved && !error && (
          <p className="text-sm text-green-700">Admin settings saved.</p>
        )}

        <button
          type="submit"
          disabled={submitting || loading}
          className="h-10 px-4 bg-vg-navy text-white text-sm font-medium hover:bg-vg-cyan disabled:opacity-50"
          data-testid="admin-settings-save"
        >
          {submitting ? "Saving…" : "Save changes"}
        </button>
      </form>
    </div>
  );
};

export default AdminSettingsPage;
